import { Button } from '@components/Button'
import {
  PAYMENT_OVERDUE_STATUS,
  PAYMENT_PAID_STATUS,
  PAYMENT_WAITING_PAYMENT_STATUS,
} from '@constants/payment'
import { useOrderDetails } from '@contexts/OrderDetailsContext'
import { useBankSlipPrint } from '@hooks/handleBankSlip'
import { useClipboard } from '@hooks/useClipboard'
import { useReactivatePayment } from '@hooks/usePayment'
import { useToastController } from '@tamagui/toast'
import camelCase from 'lodash/camelCase'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { H3, Stack, Text, View, XStack, YStack } from 'tamagui'
import { StatusBadge } from './StatusBadge'

const PaymentStatusList = {
  overdue: 212,
  waiting_payment: 124,
  paid: 205,
}

// HET-3810 — recobrança pelo aluno, com os MESMOS gates do painel do produtor
// (Xavier: BolixPaymentActions + RecoveryPaymentBolixButton + SalesDetailsMixin):
//   - só boleto ou pix; pix só na Trio (pix Adyen/Yuno não tem recobrança no gateway);
//   - só com o 1º pagamento pago/confirmado;
//   - botão visível em vencido/aguardando, habilitado só em vencido.
const REACTIVATABLE_METHODS: string[] = ['pix', 'bank_slip']
const FIRST_PAYMENT_PAID_STATUSES: string[] = [PAYMENT_PAID_STATUS, 'confirmed']
const OPEN_PAYMENT_STATUSES: string[] = [
  PAYMENT_OVERDUE_STATUS,
  PAYMENT_WAITING_PAYMENT_STATUS,
]
const TRIO_GATEWAY = 'trio'

type PaymentRow = ReturnType<typeof useOrderDetails>['payments'][number]

export function PaymentList() {
  const { t } = useTranslation('OrderDetails')
  const { printBankSlip } = useBankSlipPrint()
  const { copyToClipboard } = useClipboard()
  const { isSubscription, isStrategySales, payments, refetch } =
    useOrderDetails()
  const { handleReactivate } = useReactivatePayment()
  const toast = useToastController()
  const [reactivatingId, setReactivatingId] = useState<number | null>(null)

  if (!isSubscription && !isStrategySales) return null

  // Só para o gate da recobrança (HET-3810); os botões de copiar/imprimir não
  // checam status aqui (ver comentário do HET-3806 abaixo).
  const isPaymentOpen = (status: string) => {
    return OPEN_PAYMENT_STATUSES.includes(status)
  }

  const dynamicMarginLeft = (status: string) => {
    return PaymentStatusList[status as keyof typeof PaymentStatusList]
  }

  const isFirstPaymentPaid = FIRST_PAYMENT_PAID_STATUSES.includes(
    payments[0]?.status,
  )

  const canReactivate = (payment: PaymentRow) =>
    isFirstPaymentPaid &&
    REACTIVATABLE_METHODS.includes(payment.paymentMethod) &&
    isPaymentOpen(payment.status) &&
    (payment.paymentMethod !== 'pix' || payment.gatewayName === TRIO_GATEWAY)

  const reactivatePayment = async (payment: PaymentRow) => {
    const method = camelCase(payment.paymentMethod)
    setReactivatingId(payment.id)

    try {
      await handleReactivate(payment.id)
      toast.show(t(`payments.reactivate.success.${method}`), {
        type: 'success',
      })
    } catch (error) {
      console.error(error)
      toast.show(t(`payments.reactivate.error.${method}`), { type: 'error' })
    } finally {
      setReactivatingId(null)
      // Como no Xavier: recarrega a lista sempre (sucesso ou erro) — o novo QR/boleto
      // e o status "aguardando" vêm do backend.
      await refetch()
    }
  }

  // HET-3806: quem zera `bankSlipURL` e `pixQrCode` fora da cobrança aberta é o
  // formatPayments, então aqui basta o código existir. Antes esta tela checava
  // status por conta própria e discordava do resto: o boleto olhava só
  // `waiting_payment`, então boleto vencido — que o banco ainda aceita — ficava
  // com URL e sem botão.
  return (
    <YStack gap="$size.spacing-xs">
      <H3 color="$text-primary" ff="$mulishBold" fontSize="$font-size-md">
        {t('payments.title')}
      </H3>
      <YStack gap="$size.spacing-xxxs">
        <XStack
          gap="$size.spacing-xxxs"
          $gtSm={{
            gap: '$size.spacing-xsl',
          }}
        >
          <Text
            fontSize="$font-size-xxs"
            color="$accent"
            textTransform="uppercase"
            ff="$mulishBold"
            width={105}
          >
            {t('payments.paymentDate')}
          </Text>

          <Text
            fontSize="$font-size-xxs"
            color="$accent"
            textTransform="uppercase"
            ff="$mulishBold"
            width={100}
          >
            {t('payments.amount')}
          </Text>
        </XStack>
        <YStack rowGap="$size.spacing-xs">
          {payments.map((payment) => {
            const {
              id,
              amount,
              paymentDate,
              status,
              bankSlipURL,
              pixQrCode,
              paymentMethod,
            } = payment

            return (
              <Stack
                key={id ?? paymentDate}
                p="$size.spacing-xs"
                br="$border-radius-sm"
                borderWidth="$borderWidth.border-width-hairline"
                borderColor="$border-strong"
                rowGap="$size.spacing-xxs"
                $gtSm={{
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <XStack
                  gap="$size.spacing-xxxs"
                  flexWrap="wrap"
                  $gtSm={{
                    gap: '$size.spacing-xsl',
                  }}
                >
                  <Text
                    color="$text-primary"
                    ff="$mulishBold"
                    fontSize="$font-size-xxs"
                    width={83}
                  >
                    {paymentDate}
                  </Text>

                  <Text
                    color="$text-primary"
                    ff="$mulishRegular"
                    fontSize="$font-size-xxs"
                    width={100}
                  >
                    {amount}
                  </Text>
                  <StatusBadge status={status} />
                </XStack>
                {bankSlipURL && (
                  <Button
                    $gtSm={{
                      marginLeft: 'auto',
                    }}
                    $gtMd={{
                      marginLeft: dynamicMarginLeft(status),
                    }}
                    variant="primary"
                    onPress={() => printBankSlip(bankSlipURL)}
                  >
                    {t('payments.bankSlip')}
                  </Button>
                )}
                {pixQrCode && (
                  <View
                    $gtSm={{
                      marginLeft: 'auto',
                    }}
                    $gtMd={{
                      marginLeft: dynamicMarginLeft(status),
                    }}
                  >
                    <Button
                      variant="primary"
                      onPress={() => copyToClipboard(pixQrCode)}
                      maxWidth={150}
                      w="100%"
                    >
                      {t('payments.pixCode')}
                    </Button>
                  </View>
                )}
                {canReactivate(payment) && (
                  <View
                    $gtSm={{
                      marginLeft: 'auto',
                    }}
                  >
                    <Button
                      variant="primary"
                      testID={`reactivate-payment-button-${id}`}
                      disabled={
                        status !== PAYMENT_OVERDUE_STATUS ||
                        reactivatingId === id
                      }
                      loading={reactivatingId === id}
                      onPress={() => reactivatePayment(payment)}
                      maxWidth={170}
                      w="100%"
                    >
                      {t(
                        `payments.reactivate.label.${camelCase(paymentMethod)}`,
                      )}
                    </Button>
                  </View>
                )}
              </Stack>
            )
          })}
        </YStack>
      </YStack>
    </YStack>
  )
}
